import { interval, map, Observable, startWith, takeWhile } from 'rxjs';
import { Time } from '@enums';

const SECONDS_PER_MINUTE = 60;

/** Formats whole seconds as `m:ss` (e.g. 300 → `5:00`, 59 → `0:59`). */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / SECONDS_PER_MINUTE);
  const seconds = safe % SECONDS_PER_MINUTE;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Whole seconds left until `deadline` (epoch ms), never negative. */
export function secondsUntil(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / Time.ONE_SECOND));
}

/**
 * Emits the time left until `deadline` (epoch ms) as `m:ss` once per second
 * (the first value synchronously) and completes after emitting `0:00`. Every tick is recomputed from the
 * clock, so a delayed tick never accumulates drift.
 */
export function countdown$(
  deadline: number,
  now: () => number = () => Date.now(),
): Observable<string> {
  return interval(Time.ONE_SECOND).pipe(
    startWith(0),
    map(() => secondsUntil(deadline, now())),
    takeWhile(remaining => remaining > 0, true),
    map(formatCountdown),
  );
}
